/**
 * Flock Manager hook.
 *
 * Pro users: birds sync to MongoDB via /api/flock (unlimited).
 * Free users: birds live in browser localStorage (capped at 2).
 *
 * Identical CRUD interface for both, so callers can be agnostic.
 *
 * Bird shape:
 *   {
 *     id:           string,
 *     name:         string,
 *     ring_number:  string | null,
 *     sex:          "male" | "female",
 *     hatch_date:   string | null,   // YYYY-MM-DD
 *     options:      object,          // calculator parent.options
 *     notes:        string | null,
 *     created_at:   ISO string,
 *     updated_at:   ISO string,
 *   }
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { usePro } from "./pro";

const LS_KEY = "budgie_flock_v1";
export const FREE_BIRD_LIMIT = 2;

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function writeLocal(list) {
  localStorage.setItem(LS_KEY, JSON.stringify(list));
}

function uid() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useFlock() {
  const { isPro, checking } = usePro();
  const [birds, setBirds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    if (checking) return;
    if (!isPro) {
      setBirds(readLocal());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/flock");
      setBirds(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load flock");
      setBirds([]);
    } finally {
      setLoading(false);
    }
  }, [isPro, checking]);

  useEffect(() => {
    const t = setTimeout(() => { refresh(); }, 0);
    return () => clearTimeout(t);
  }, [refresh]);

  const create = useCallback(
    async (payload) => {
      if (!isPro) {
        const cur = readLocal();
        if (cur.length >= FREE_BIRD_LIMIT) {
          throw new Error(`Free plan saves up to ${FREE_BIRD_LIMIT} birds. Upgrade to Pro for unlimited.`);
        }
        const now = new Date().toISOString();
        const bird = { id: uid(), ...payload, created_at: now, updated_at: now };
        const next = [bird, ...cur];
        writeLocal(next);
        setBirds(next);
        return bird;
      }
      const { data } = await api.post("/flock", payload);
      setBirds((b) => [data, ...b]);
      return data;
    },
    [isPro],
  );

  const update = useCallback(
    async (id, payload) => {
      if (!isPro) {
        const now = new Date().toISOString();
        const next = readLocal().map((b) =>
          b.id === id ? { ...b, ...payload, updated_at: now } : b,
        );
        writeLocal(next);
        setBirds(next);
        return next.find((b) => b.id === id);
      }
      const { data } = await api.put(`/flock/${id}`, payload);
      setBirds((b) => b.map((x) => (x.id === id ? data : x)));
      return data;
    },
    [isPro],
  );

  const remove = useCallback(
    async (id) => {
      if (!isPro) {
        const next = readLocal().filter((b) => b.id !== id);
        writeLocal(next);
        setBirds(next);
        return;
      }
      await api.delete(`/flock/${id}`);
      setBirds((b) => b.filter((x) => x.id !== id));
    },
    [isPro],
  );

  const limitReached = !isPro && birds.length >= FREE_BIRD_LIMIT;

  return {
    birds,
    loading,
    error,
    isPro,
    limitReached,
    refresh,
    create,
    update,
    remove,
  };
}
